import { isa } from "@p0security/util/types/is";

import { RegisteredIntegrationKeys } from "../integrations/registry-keys.ts";
import type { RegisteredIntegrationKey } from "../integrations/registry-keys.ts";
import type { ClientError, UpstreamError } from "./error.ts";
import type { Evidence } from "./evidence.ts";
import type { Actor } from "./index.ts";
import type { ApprovalWithContainingRules } from "./policy/evaluation/types.ts";
import type { Policy } from "./policy/types.ts";

/** The key of an integration a permission can be requested against.
 *
 * Deliberately `string`: closing this union over the registry would make every
 * consumer of a permission depend on all 37 registration modules. Narrow with
 * {@link isPermissionType} where the closed set is genuinely needed.
 */
export type PermissionType = string;

/** The subset of {@link PermissionType} the registry declares. Only for code
 * that needs the closed set — policy validation and evaluation, and
 * `@p0security/core`'s abstract resource pool. Everything else should take a
 * `PermissionType`.
 */
export type RegisteredPermissionType = RegisteredIntegrationKey;

export const isPermissionType = (
  value: string
): value is RegisteredPermissionType => isa(RegisteredIntegrationKeys, value);

export type BasePermission = {
  approvedOnly?: boolean;
  commandLine?: string;
  /** The duration actually applied to this request. */
  duration?: string;
  reason?: string;
  requestedTimestamp: number;
  /** The duration originally requested by the user. May be clamped down to
   * the maximum allowed duration — present only when that happened, so its
   * presence indicates the request's duration was reduced from what was asked for. */
  originalRequestedDuration?: string;
  /** The duration originally submitted by the approver, before it was clamped
   * down to the maximum allowed duration — present only when that happened, so
   * its presence indicates the approver's submitted duration was reduced. */
  originalApprovedDuration?: string;
  /** Internal index of the Grants in this Request's delegation tree, one `{type}|{access}` key each
   *
   * Written once at creation (see `toGrantKeys` in `@p0security/core`); no state transition
   * changes it. Never returned by the API.
   */
  grantKeys?: string[];
};

export type Permission = {
  type: string;
  access: string;
  delegation: { key: string; request: any }[];
  permission: Record<string, unknown>;
};

export type StagedPermission = Permission & {
  generated: Record<string, unknown> | object;
};

export type PermissionDelegation = Pick<
  Permission,
  "access" | "delegation" | "permission"
> & {
  generated?: StagedPermission["generated"];
};

export type DelegationBranch = PermissionDelegation & Pick<Permission, "type">;

export type RequestId<T extends Permission> = T & {
  requestId: string;
};

export interface PermissionRequestError {
  type?: ClientError["type"] | UpstreamError["type"];
  message: string;
}

export type EmailNotificationData = Record<
  string, // the email address of the recipient
  {
    subject: string;
    messageId?: string;
  }
>;

export type EmailNotifications = {
  approvalEmailData?: EmailNotificationData;
  requestorEmailData?: EmailNotificationData;
};

export type MsTeamsNotifications = {
  /** A universally accessible URL for the message */
  approvalConversationUrl?: string;
  // TODO: use the actual type "Activity" from the MS Teams SDK.
  /** An activity reference to the channel message */
  approvalActivity?: any;
  /** A list of activity reference to the user messages */
  approvalActivityList?: any;
  /** An activity reference to the requestor's direct message */
  requestorActivity?: any;
};

export type SlackNotifications = {
  /** Integration identifier for the approval message
   *
   * In Slack, this is `message_ts`
   */
  approvalMessageId?: string;
  /** A universally accessible URL for the message */
  approvalConversationUrl?: string;
  /** Info about all the approver notifications that were sent via DM, if applicable */
  approverNotifications?: SlackMessage[];
  /** Email of user that approved this request */
  approvalByEmail?: string;
  /** ID of the user that approved this request */
  approvalByUserId?: string;
  /** Timestamp when the request was approved */
  approvedTimestamp?: number;
  /** Channel ID of the requestor's DM */
  requestorChannelId?: string;
  /** Message ID of the requestor conversation */
  requestorMessageId?: string;
};

export type SlackMessage = { channelId: string; messageId: string };

// capture the approver details irrespective of where it was approved from
// in future the approval even if the approval happens from the web
// this removes the need for conditional checks to understand how the request got approved.
export type ApprovalDetails = {
  /** Source ID of the entity that approved this request */
  id: string;
  /** Name of the entity that approved this request */
  name?: string;
  /** Email of the user that approved this request */
  email?: string;
  /** Timestamp when the approver clicked the Approve button */
  approvedTimestamp?: number;
  /** Any additional information about how this request was approved. May or may
   * not be present depending on the approval source.
   */
  extra?: Record<string, string | undefined>;
  // Written generically by notifiers outside the closed set below (e.g.
  // evidence/pagerduty/incidentio/persistent/slack/webapp are only the known writers).
  approvalSource: string;
};

// Kept separate from ApprovalDetails so revoking never overwrites the
// approver captured when the request was approved.
export type RevocationDetails = {
  /** Source ID of the user that revoked this request */
  id: string;
  /** Name of the user that revoked this request */
  name?: string;
  /** Email of the user that revoked this request */
  email: string;
  /** Timestamp when the revoker clicked the Revoke button */
  revokedTimestamp: number;
  revocationSource: RequestNotificationKey | "api" | "webapp";
};

// Written generically per-integration (e.g. a per-integration escalation
// notifier key), not limited to the two known writers below.
export type EscalationDetails = Record<string, string[]>;

// The known notification sub-objects, keyed by notifier. `msTeams` is
// deliberately not aligned to the `ms-teams` registry key: the sub-key is
// persisted into `RevocationDetails.revocationSource` and into audit events'
// `data.source`, an append-only log no migration has ever rewritten.
type KnownRequestNotifications = {
  msTeams?: MsTeamsNotifications;
  slack?: SlackNotifications;
  email?: EmailNotifications;
};

export type RequestNotificationKey = keyof KnownRequestNotifications;
export type RequestNotificationValue =
  KnownRequestNotifications[RequestNotificationKey];

// Intersected with an open index signature so notifiers outside the known
// set (e.g. pagerduty, incidentio) can still write their own sub-object,
// while every known notifier keeps precise read/write types for its own key.
export type RequestNotifications = KnownRequestNotifications &
  Record<string, RequestNotificationValue | undefined>;

export type AgenticContext = {
  /** Unique identifier for the gateway MCP session that initiated this request */
  sessionId?: string;
  /** Agentic session identity (minted by the agentic gateway on a per-MCP-session basis) */
  session: Actor;
  agents: Actor[];
  /** Installed gateway component ID that issued the session token */
  gatewayId?: string;
  /** Installed identity-provider component ID that authenticated the agent (federated flows only) */
  identityProviderId?: string;
  /** True when the request was made by a headless agent (no human user in the delegation chain) */
  headless?: boolean;
};

export type RequestData = {
  error?: PermissionRequestError;
  /** When `error` is set, the status the request was in when it errored. Used to
   * offer targeted recovery actions (e.g. retrying a failed revoke). */
  erroredFromStatus?: string;
  expiryTimestamp?: number;
  grantTimestamp?: number; // Timestamp when the request was granted
  revokedTimestamp?: number; // Timestamp when the request was expired
  // Only those documents are retrieved by the expiry service where `isAwaitingExpiry`is true.
  // This field is not set (undefined) before a permission is granted in the target resource (DONE status).
  isAwaitingExpiry?: boolean;
  evidence?: Evidence[];
  lastUpdatedByUser?: string;
  lastUpdatedTimestamp?: number;
  notifications?: RequestNotifications;
  principal: string;
  requestor: string;
  /** If present, list of agents that initiated this request */
  agentic?: AgenticContext;
  status: string; // Must be a "RequestStatus"
  /** If present, the time after which this request can no longer be approved */
  approvableUntil?: number;
  approvalDetails?: ApprovalDetails;
  /** If present, details of the user-initiated revocation of this request */
  revocationDetails?: RevocationDetails;
  canEscalate?: boolean;
  escalationDetails?: EscalationDetails;

  // Contains a snapshot of policies which applied to the given request, populated when the request is approved,
  // for audit purposes.
  policiesSnapshot?: Policy[];
  // contains the approval rules that were applied to the request if the request is approved
  allowedApprovals?: ApprovalWithContainingRules[];
};
export type StagedPermissionRequest = BasePermission &
  StagedPermission &
  RequestData;

export type PermissionRequest = BasePermission & Permission & RequestData;

export const isStagedPermissionRequest = (
  request: PermissionRequest
): request is StagedPermissionRequest => "generated" in request;

// In order to update a nested path without overriding that path's parents, must
// use dot notation (see DocumentReference.update)
export type PermissionRequestUpdates = Partial<
  Omit<
    StagedPermissionRequest,
    "grantKeys" | "isAwaitingExpiry" | "notifications"
  > & {
    // TODO: use the actual type "Activity" from the MS Teams SDK. The SDK is not available in the shared package.
    "notifications.msTeams.requestorActivity": any;
    "notifications.msTeams.approvalActivity": any;
    "notifications.msTeams.approvalActivityList": any;
    "notifications.msTeams.approvalByUserId": string;
    "notifications.msTeams.approvalByEmail": string;
    "notifications.msTeams.approvedTimestamp": number;
    "notifications.msTeams.approvalConversationUrl": string;
    "notifications.slack.approvalMessageId": string;
    "notifications.slack.approvalByUserId": string;
    "notifications.slack.approvalByEmail": string;
    "notifications.slack.approvedTimestamp": number;
    "notifications.slack.approvalConversationUrl": string;
    "notifications.slack.requestorChannelId": string;
    "notifications.slack.approverNotifications": SlackMessage[];
    "notifications.slack.requestorMessageId": string;
    "notifications.email.requestorEmailData": EmailNotificationData;
    "notifications.email.approvalEmailData": EmailNotificationData;
    "approvalDetails.id": string;
    "approvalDetails.name": string;
    "approvalDetails.email": string;
    "approvalDetails.approvalSource": string;
    "approvalDetails.approvedTimestamp": number;
    "revocationDetails.id": string;
    "revocationDetails.name": string;
    "revocationDetails.email": string;
    "revocationDetails.revocationSource": RevocationDetails["revocationSource"];
    "revocationDetails.revokedTimestamp": number;
    "generated.publicKey": string;
  }
>;

export const PermissionKeys = ["permission", "type", "access"];
